import cloneDeep from "lodash/cloneDeep";
import { getSubDomain, convertDateTimezone } from "utils/helperFunctions";
import exploreAssessmentsImage from "resources/images/Criteria-AppCues-FreeTrialWelcome-DashboardThumb-Assessments.png";
import createAJobImage from "resources/images/Criteria-AppCues-FreeTrialWelcome-DashboardThumb-CreateJob.png";
import exploreInterviewsImage from "resources/images/Criteria-AppCues-FreeTrialWelcome-DashboardThumb-Interview.png";
import exploreDevelopImage from "resources/images/Criteria-AppCues-FreeTrialWelcome-DashboardThumb-Develop.png";
import { faChartLine, faLightbulbOn } from "@fortawesome/pro-regular-svg-icons";
import moment from "moment-timezone";
import { parseISO } from "date-fns";

// Video Interview Sorting Methods
const dateRecent = (candidates) =>
  candidates.sort((candidate1, candidate2) =>
    candidate1.get("dateCompleted") < candidate2.get("dateCompleted") ? 1 : -1
  );

const firstName = (candidates) =>
  candidates.sort((candidate1, candidate2) => {
    if (
      candidate1.get("firstName").toLowerCase() !==
      candidate2.get("firstName").toLowerCase()
    ) {
      return candidate1
        .get("firstName")
        .localeCompare(candidate2.get("firstName"));
    }
    return candidate1.get("lastName").localeCompare(candidate2.get("lastName"));
  });

const lastName = (candidates) =>
  candidates.sort((candidate1, candidate2) => {
    if (
      candidate1.get("lastName").toLowerCase() !==
      candidate2.get("lastName").toLowerCase()
    ) {
      return candidate1
        .get("lastName")
        .localeCompare(candidate2.get("lastName"));
    }
    return candidate1
      .get("firstName")
      .localeCompare(candidate2.get("firstName"));
  });

const jobTitle = (candidates) =>
  candidates.sort((candidate1, candidate2) =>
    candidate1.get("jobTitle").localeCompare(candidate2.get("jobTitle"))
  );

const overallStatus = (candidates) => {
  const covertStatusToNumber = {
    Pending: 0,
    Complete: 1,
  };
  const sortedArr = candidates.sort(
    (candidate1, candidate2) =>
      covertStatusToNumber[candidate1.get("overallStatus")] -
      covertStatusToNumber[candidate2.get("overallStatus")]
  );
  return sortedArr;
};

const userEvaluationStatus = (candidates) =>
  candidates.sort((candidate1, candidate2) => {
    if (candidate1.get("isEvaluator") && candidate2.get("isEvaluator")) {
      return (
        candidate1.get("currentUserEvaluationStatus") -
        candidate2.get("currentUserEvaluationStatus")
      );
    }
    return candidate1.get("isEvaluator") - candidate2.get("isEvaluator") > 0
      ? -1
      : 1;
  });

/* -------------------------------- Exported Functions -------------------------------- */
export const getFilteredVideoInterviewCandidates = (
  candidates,
  filterOptions
) => {
  let candidatesFiltered = cloneDeep(candidates);
  if (filterOptions.jobTitle && filterOptions.jobTitle !== "") {
    candidatesFiltered = candidatesFiltered.filter(
      (candidate) => candidate.get("jobTitle") === filterOptions.jobTitle
    );
  }

  if (filterOptions.jobTitleInternal && filterOptions.jobTitleInternal !== "") {
    candidatesFiltered = candidatesFiltered.filter(
      (candidate) =>
        candidate.get("jobTitleInternal") === filterOptions.jobTitleInternal
    );
  }

  if (
    filterOptions.userEvaluationStatus &&
    filterOptions.userEvaluationStatus !== ""
  ) {
    const convertStatusToBoolean = {
      Pending: false,
      Evaluated: true,
    };

    candidatesFiltered = candidatesFiltered.filter((candidate) => {
      if (filterOptions.userEvaluationStatus === "Not Required") {
        return !candidate.get("isEvaluator");
      }
      return (
        candidate.get("currentUserEvaluationStatus") ===
          convertStatusToBoolean[filterOptions.userEvaluationStatus] &&
        candidate.get("isEvaluator")
      );
    });
  }

  if (filterOptions.overallStatus && filterOptions.overallStatus !== "") {
    candidatesFiltered = candidatesFiltered.filter(
      (candidate) =>
        candidate.get("overallStatus") === filterOptions.overallStatus
    );
  }

  if (filterOptions.scheduledBy && filterOptions.scheduledBy.length > 0) {
    candidatesFiltered = candidatesFiltered.filter((candidate) =>
      filterOptions.scheduledBy.find(
        (user) => user.value === candidate.get("scheduledBy")
      )
    );
  }
  return candidatesFiltered;
};

export const sortVideoInterviewCandidates = (CandidatesObj, sortMethod) => {
  switch (sortMethod) {
    case "Date Completed - Recent":
      return dateRecent(CandidatesObj);
    case "First Name - A to Z":
      return firstName(dateRecent(CandidatesObj));
    case "Last Name - A to Z":
      return lastName(dateRecent(CandidatesObj));
    case "Job Title":
      return jobTitle(dateRecent(CandidatesObj));
    case "Overall Status":
      return overallStatus(dateRecent(CandidatesObj));
    case "My Evaluation Status":
      return userEvaluationStatus(dateRecent(CandidatesObj));
    default:
      return dateRecent(CandidatesObj);
  }
};

/* ----------- User status (role and permissions) for Candidates Table ----------- */
export const getCandidateStatus = (
  props,
  resultsPermission,
  isVideoInterviewEvaluator,
  jobPermission
) => {
  const {
    candidatesLoaded,
    candidates,
    hasCandidates,
    recentJobs,
    activeJobsCount,
  } = props;

  if (!resultsPermission) {
    return isVideoInterviewEvaluator
      ? "noResultsPermissionButEvaluator"
      : "noResultsPermission";
  }

  if (!candidatesLoaded || candidates.size) {
    // !candidatesLoaded shows loading candidate table, candidates.size shows loaded candidates in table
    return null;
  }

  if (recentJobs.size > 0 || activeJobsCount) {
    return hasCandidates ? "hasCandidatesHasJobs" : "noCandidatesHasJobs";
  }

  if (!hasCandidates) {
    return jobPermission
      ? "noCandidatesHasPermission"
      : "noCandidatesNoPermission";
  }

  return null;
};

export const getJobStatus = (props, resultsPermission) => {
  const { candidatesLoaded, candidates } = props;

  if (!resultsPermission) {
    return "noResultsPermission";
  }
  if (!candidatesLoaded) {
    return null; // shows loading jobs table
  }

  return candidates.size ? "hasPermissionHasJobs" : "hasPermissionNoJobs";
};

export const getFreeTrialHomeActionCardData = (isFreeTrial) => {
  const localeInstance = getSubDomain() === "app-au" ? "AU" : "NA";
  const isFreeTrialUser = isFreeTrial ? "ft" : "";

  return {
    exploreAssessments: {
      title: "Explore Assessments",
      introduction:
        "Preview our science-backed assessments to see the candidates experience and the insights you receive. Our job-relevant assessments are grounded in science and backed by over a billion data points.",
      image: exploreAssessmentsImage,
      primaryBtnVariant: "primary",
      primaryBtnText: "Assessment Library",
      secondaryBtnText: "See Success Stories",
      secondaryBtnIcon: faChartLine,
      eventTriggerName: "success_stories_modal",
      eventTriggerAttributes: {
        surface_area: "checklist",
        user_type: isFreeTrialUser,
        instance: localeInstance,
      },
      eventTriggerFallbackUrl:
        "https://www.criteriacorp.com/resources/case-studies",
      cardBorderColor: "#001F4E",
    },
    createAJob: {
      title: "Create a Job",
      introduction:
        "Explore Criteria's recommended assessments for the roles you are hiring for or choose your own assessments. You can try the assessments and view your results.",
      image: createAJobImage,
      primaryBtnVariant: "primary",
      primaryBtnText: "Create a Job",
      cardBorderColor: "#001F4E",
    },
    exploreInterviews: {
      title: "Explore Interviews",
      introduction:
        "Deliver on-brand video interviews that cut screening time by up to 75%. Make your  interviews consistent, professional, and 2x more predictive of job success.",
      image: exploreInterviewsImage,
      secondaryBtnText: "Learn More",
      secondaryBtnIcon: faLightbulbOn,
      cardBorderColor: "#CBCCCD",
      eventTriggerName: "si_modal",
      eventTriggerAttributes: {
        surface_area: "checklist",
        user_type: isFreeTrialUser,
        instance: localeInstance,
      },
      eventTriggerFallbackUrl: "https://www.criteriacorp.com/interview",
    },
    exploreDevelop: {
      title: "Explore Develop",
      introduction:
        "Take teams and managers from good to great with personalized AI coaching and skills development that uses our science-backed approach.",
      image: exploreDevelopImage,
      primaryBtnVariant: "outline-primary",
      primaryBtnText: "Try Develop",
      secondaryBtnText: "Learn More",
      secondaryBtnIcon: faLightbulbOn,
      cardBorderColor: "#CBCCCD",
      eventTriggerName: "develop_modal",
      eventTriggerAttributes: {
        surface_area: "checklist",
        user_type: isFreeTrialUser,
        instance: localeInstance,
      },
      eventTriggerFallbackUrl: "https://criteriacorp.com/develop",
    },
  };
};

export const getFreeTrialExperimentCompanyAccountIds = () => {
  const localeInstance = getSubDomain() === "app-au" ? "AU" : "NA";

  const FT_DASHBOARD_EXPERIMENT_COMPANY_ACCOUNT_IDS_AU = [18341];
  const FT_DASHBOARD_EXPERIMENT_COMPANY_ACCOUNT_IDS_NA = [244585, 245642];

  return localeInstance === "AU"
    ? FT_DASHBOARD_EXPERIMENT_COMPANY_ACCOUNT_IDS_AU
    : FT_DASHBOARD_EXPERIMENT_COMPANY_ACCOUNT_IDS_NA;
};

/**
 * Checks if the user/company is eligible for the free trial experiment
 * @param {Object} userData - The user data containing isFreeTrial and dateAccountCreated
 * @returns {boolean} - Whether the free trial experiment is enabled for this user
 */
export const getIsFreeTrialExperimentEnabled = (userData) => {
  if (!userData) {
    return false;
  }

  const convertedDateCompanyAccountCreated = convertDateTimezone(
    userData.dateTrialStarted,
    false
  );

  const freeTrialExperimentActiveDate = parseISO(
    moment(moment.tz("2025-04-01 00:00:00", "America/Los_Angeles")).format(
      "YYYY-MM-DD HH:mm:ss"
    )
  );

  const isCompanyInFreeTrialExperiment =
    getFreeTrialExperimentCompanyAccountIds().includes(
      userData.companyAccountId
    );

  return (
    userData?.isFreeTrial &&
    (isCompanyInFreeTrialExperiment ||
      new Date(convertedDateCompanyAccountCreated) >
        freeTrialExperimentActiveDate)
  );
};
